/**
 * Console analytics provider for debugging.
 *
 * This provider logs all events to the console, making it useful for development and debugging.
 */

import type { AnalyticsProvider } from '../types/providers.js';
import type { AnalyticsEvent } from '../types/events.js';
import { config } from '#~/config/config.js';

export class ConsoleProvider implements AnalyticsProvider {
  name = 'console';
  private initialized = true;

  /**
   * Initialize the console provider.
   * This is a no-op since there's nothing to initialize.
   */
  async initialize(): Promise<void> {
    this.initialized = true;
  }

  /**
   * Log an event to the console.
   *
   * @param event The event to log
   */
  async track(event: AnalyticsEvent): Promise<void> {
    if (config.env !== 'prod') {
      console.group('📊 Analytics Event');
      console.log('Name:', event.name);
      console.log('Category:', event.category);
      console.log('Properties:', event.properties);
      console.log('Timestamp:', new Date(event.timestamp || Date.now()).toISOString());
      console.groupEnd();
    }
  }

  /**
   * Log a user identification to the console.
   *
   * @param userId The user's ID
   * @param traits Optional user traits/properties
   */
  async identify(userId: string, traits?: Record<string, any>): Promise<void> {
    if (config.env !== 'prod') {
      console.group('👤 Analytics Identify');
      console.log('User ID:', userId);
      console.log('Traits:', traits);
      console.groupEnd();
    }
  }

  /**
   * Log a reset to the console.
   */
  async reset(): Promise<void> {
    if (config.env !== 'prod') {
      console.log('🔄 Analytics Reset');
    }
  }

  /**
   * Check if the console provider is initialized.
   * This always returns true since there's nothing to initialize.
   */
  isInitialized(): boolean {
    return this.initialized;
  }
}
